package ar.corzo.health_tracker.utils

import ar.corzo.health_tracker.types.NaturalRange
import ar.corzo.health_tracker.types.NaturalRange.Companion.takeIfIn
import kotlinx.datetime.Instant
import kotlin.collections.List
import kotlin.contracts.ExperimentalContracts
import kotlin.contracts.contract
import kotlin.time.Duration.Companion.milliseconds

fun <A,B,F: Function1<A, B>> callbackIfTrue(condition: Boolean, callback: F) = callback.takeIf { condition }

@OptIn(ExperimentalContracts::class)
inline fun <T, R> List<T>.fastZipWithNext(transform: (T,T) -> R): List<R>? {
    return fastZipWithNextIndexed{(a),(b)->
        transform(a, b)
    }
}
@OptIn(ExperimentalContracts::class)
inline fun <T, R> List<T>.fastZipWithNextIndexed(transform: (Pair<T,Int>,Pair<T,Int>) -> R): List<R>? {
    contract { callsInPlace(transform) }
    if(size <= 1) return null
    val result = mutableListOf<R>()
    var current = get(0)
    // `until` as we don't want to invoke this for the last element, since that won't have a `next`
    for (i in 0 until lastIndex) {
        val nextIndex=i+1
        val next=get(nextIndex)
        result.add(transform(current to i, next to nextIndex))
        current = next
    }
    return result
}

fun <T> Collection<T>.indexOfOrNull(element: T?):Int?{
    val index=indexOf(element)
    return if(index<0) null else index
}
fun <T> Collection<T>.indexOfOrNull(transform: (T)->Boolean):Int?{
    val index = indexOfFirst(transform)
    return if(index<0) null else index
}

infix fun List<Any>.joinWith(connector:String):String=joinToString(connector)
infix fun Pair<Any,Any>.joinWith(connector:String):String=toList().joinWith(connector)


fun <T> List<T>.safeSlice(indices: IntRange): List<T>? {
    if(indices.isEmpty()) return null
    return this.safeSubList(indices.first, indices.last + 1)//.takeIf{ it.isNotEmpty() }
}
private fun <T> List<T>.safeSubList(fromIndex: Int, toIndex: Int): List<T> = this.subList(fromIndex.coerceAtLeast(0), toIndex.coerceAtMost(this.size))

inline fun <T: Any, R : Comparable<R>> List<T>.groupSorted(
    crossinline selector: (T) -> R
):List<List<T>>{
    val result = mutableListOf<List<T>>()
    forEach { element ->
        val lastGroup = result.lastOrNull()
        if(lastGroup!=null && selector(lastGroup.last()) < selector(element))
            result[result.lastIndex] = lastGroup+element
        else result.add(listOf(element))
    }
    return result
}
inline fun <T: Any, R : Comparable<R>> List<T>.lastGroupSorted(
    crossinline selector: (T) -> R
):List<T>{
    if(size<=1) return this

    val result = mutableListOf<T>()
    var i = lastIndex
    var signChange = false
    while(i > 0 && !signChange) {
        val element = get(i)
        val prevElement = get(i-1)

        if(selector(prevElement) <= selector(element)) {
            result.add(element)
        }else signChange = true

        i--
    }
    return result
}

fun Int.sortRangeTo(other: Int) = if(this < other) this..other else other..this
infix fun Double.sortRangeTo(other: Double) = if(this < other) this..other else other..this
fun Float.sortRangeTo(other: Float) = if(this < other) this..other else other..this
/**
 * @param acceptedElementsNotSorted require >= 0
 * @return true if the list is sorted with a margin error, or false if it is empty
 * */
inline fun <T: Any, R : Comparable<R>> List<T>.isSorted(
    acceptedElementsNotSorted: Int = 0,
    crossinline selector: (T) -> R
): Boolean {
    require(acceptedElementsNotSorted >= 0)

    if(isEmpty()) return false
    if(size==1) return true

    var elementsNotSorted = 0
    var i = 0
    while(i < lastIndex && elementsNotSorted <= acceptedElementsNotSorted+1){
        val current = get(i)
        val currentComparable = selector(current)

        val next = get(i+1)
        val nextComparable = selector(next)

        if(currentComparable > nextComparable) elementsNotSorted++
        i++
    }
    return elementsNotSorted <= acceptedElementsNotSorted
}

operator fun ClosedRange<Double>.contains(range: ClosedRange<Double>): Boolean{
    return range.start in this && range.endInclusive in this
}

fun <K,V, W> Map<K,V>.mapValuesNotNull(transform: (value: V) -> W?): Map<K, W> = toList().mapNotNull{it.first to (transform(it.second)?:return@mapNotNull null)}.toMap()
fun <K,V: Any> Map<K,V?>.filterNotNull(): Map<K,V> = mapValuesNotNull{it}


// cast
fun <V> Any.asMap() = (this as? Map<*,*>)?.toList()?.filterIsInstance<Pair<String,V>>()?.toMap()

inline fun <reified T> Any.asList() = (this as? List<*>)?.filterIsInstance<T>()

fun Any.asMapList() = asList<Any?>()?.map{it?.asMap<Any>()}//filterIsInstance<Map<String,*>?>()
inline fun <reified V> Any.asSetMap() = asMap<Any>()?.mapValuesNotNull{it.asList<V>()?.toSet()}

// get
fun Map<String,*>.getList(key: String) = get(key)?.asList<Any>()

fun <V> Map<String,*>.getMap(key: String) = get(key)?.asMap<V>()

fun Map<String,*>.getMapList(key: String) = get(key)?.asMapList()
inline fun <reified T> Map<String,*>.getSetMap(key: String) = get(key)?.asSetMap<T>()

fun Map<String,*>.getDate(key: String, native: Boolean) = if(native) get(key) as? Instant else getLong(key)?.milliseconds?.toInstant()

fun Map<String,*>.getString(key: String) = get(key) as? String
fun Map<String,*>.getNumber(key: String) = get(key) as? Number
fun Map<String,*>.getInt(key: String) = getNumber(key)?.toInt() ?: (get(key) as? String)?.toIntOrNull()
fun Map<String,*>.getNatural(key: String, range: NaturalRange = NaturalRange.FromOne()) = getInt(key)?.takeIfIn(range)
fun Map<String,*>.getLong(key: String) = getNumber(key)?.toLong() ?: (get(key) as? String)?.toLongOrNull()
fun Map<String,*>.getFloat(key: String) = getNumber(key)?.toFloat() ?: (get(key) as? String)?.toFloatOrNull()?.takeIf { it.isFinite() }
fun Map<String,*>.getDouble(key: String) = (getNumber(key)?.toDouble() ?: getString(key)?.toDoubleOrNull())?.takeIf { it.isFinite() }
// set
fun <K,V> MutableMap<K, V>.setIfNotNull(key: K, value: V?) {
    if (value != null) set(key, value)
}